/**
 * PostHog adapter (US cloud). Optional: with no REACT_APP_POSTHOG_KEY every
 * call is a no-op and nothing is requested. posthog-js is loaded with a
 * dynamic import so it stays out of the main bundle, and calls made before it
 * has loaded are queued.
 *
 * Privacy: no session recording, no input/form values (autocapture is limited
 * to clicks on links and buttons), Do Not Track respected, anonymous profiles
 * only, no IP stored. Every event carries site: "jk-automations.com" so it is
 * separable from the Arceus product in the same PostHog project (497389).
 */

type Props = Record<string, string | number | boolean | null | undefined>;
type Call = (ph: import('posthog-js').PostHog) => void;

export const POSTHOG_KEY: string = process.env.REACT_APP_POSTHOG_KEY || '';
export const POSTHOG_HOST = 'https://us.i.posthog.com';
export const POSTHOG_SITE = 'jk-automations.com';

let queue: Call[] = [];
let ready: import('posthog-js').PostHog | null = null;
let started = false;

const run = (call: Call) => {
  if (!POSTHOG_KEY || typeof window === 'undefined') return;
  if (ready) {
    try {
      call(ready);
    } catch {
      /* analytics must never break the page */
    }
    return;
  }
  if (queue.length < 50) queue.push(call);
  if (started) return;
  started = true;
  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: '2025-05-24',
        capture_pageview: false, // sent by hand on each SPA route change
        capture_pageleave: true,
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['a', 'button'],
        },
        disable_session_recording: true,
        disable_surveys: true,
        mask_all_text: false,
        mask_all_element_attributes: false,
        respect_dnt: true,
        person_profiles: 'identified_only',
        ip: false,
        loaded: (ph) => ph.register({ site: POSTHOG_SITE }),
      });
      ready = posthog;
      const pending = queue;
      queue = [];
      pending.forEach(run);
    })
    .catch(() => {
      queue = [];
    });
};

export const posthogCapture = (event: string, props?: Props) =>
  run((ph) => ph.capture(event, props));

export const posthogPageView = (path: string) =>
  run((ph) => ph.capture('$pageview', { path, $current_url: window.location.href }));
